import { create } from "zustand";
import {
  inputRefsIn,
  isWidgetRef,
  metricKey,
  toMetricSelection,
  type DimensionSelection, type Filter, type InputDef, type MetricSelection, type RowFilter, type SemanticQueryInput, type Visualization,
  type WidgetDefinitionInput,
} from "@cd/core";

export interface Draft {
  title: string;
  /** The data pack every field comes from; unset means the workspace's first (always, in a one-pack workspace). */
  pack?: string;
  metrics: MetricSelection[];
  dimensions: DimensionSelection[];
  filters: Filter[];
  sort: { key: string; dir: "asc" | "desc" }[];
  limit?: number;
  compareTo?: "previous_period" | "previous_year";
  /** How many periods back to compare with (default 1). */
  comparePeriods?: number;
  /** Show additive metrics as an average per day instead of a total. */
  averagePer?: "day";
  /** Work formulas out for each business day, then add the days up. */
  eachDay?: boolean;
  /** "Only show rows where…" on the widget's own columns. */
  rowFilters: RowFilter[];
  /** Dashboard inputs formulas can use as `input.<id>`. Only the ones a formula uses are saved. */
  inputs: InputDef[];
  viz: Visualization;
  /** Visualization was picked by the user (don't auto-switch it). */
  vizPinned: boolean;
  /** From natural-language ask: interpretations to confirm. */
  assumptions?: string[];
}

export const emptyDraft = (): Draft => ({
  title: "Untitled widget", metrics: [], dimensions: [], filters: [], sort: [], inputs: [], rowFilters: [], viz: { type: "kpi", config: {} }, vizPinned: false,
});

export function draftFromDefinition(def: WidgetDefinitionInput, assumptions?: string[]): Draft {
  return {
    title: def.title,
    pack: def.query.pack,
    metrics: def.query.metrics.map(toMetricSelection),
    dimensions: def.query.dimensions ?? [],
    filters: (def.query.filters ?? []).map((f) => ({ ...f, values: f.values ?? [] })),
    sort: def.query.sort ?? [],
    limit: def.query.limit,
    compareTo: def.query.compareTo,
    comparePeriods: def.query.comparePeriods,
    averagePer: def.query.averagePer,
    eachDay: def.query.eachDay,
    rowFilters: (def.query.rowFilters ?? []).map((f) => ({ ...f, values: f.values ?? [] })),
    inputs: (def.query.inputs ?? []).map((i) => ({ ...i, format: i.format ?? "number" })),
    viz: { type: def.visualization.type, config: def.visualization.config ?? {} },
    vizPinned: true,
    assumptions,
  };
}

/** A filter still being set up: no field chosen yet, or no value for an op that needs one. */
export const filterIncomplete = (f: Filter) => !f.ref || (f.op !== "is_null" && f.op !== "not_null" && !f.values.length);

/** A filtered column runs on its complete filters; until it has one, it runs as its plain metric. */
function metricToQuery(m: MetricSelection): MetricSelection {
  if (m.kind !== "metric" || !m.filters) return m;
  const filters = m.filters.filter((f) => !filterIncomplete(f));
  return filters.length ? { ...m, filters, label: m.label?.trim() || m.id } : { kind: "metric", ref: m.ref, ...(m.hidden && { hidden: true }) };
}

const slug = (s: string) => {
  const base = s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return /^[a-z]/.test(base) ? base : `c_${base || "column"}`;
};

/** An id for a new column from its name, unique among the draft's columns of that kind. */
export function columnId(d: Draft, label: string, kind: "metric" | "formula", except?: string): string {
  const taken = new Set(d.metrics.filter((m) => m.kind === kind && m.id !== except).map((m) => m.id));
  const base = slug(label);
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}_${n}`;
  return id;
}

/**
 * Make metric `i` a filtered column (its own filters; formulas use it as `filtered.<id>`), or change
 * one. Its id follows its name, and formulas and the sort that use the old id follow along. With no
 * filters left it's the plain metric again.
 */
export function setColumnFilters(d: Draft, i: number, patch: { label?: string; filters?: Filter[] }): Draft {
  const m = d.metrics[i];
  if (m?.kind !== "metric") return d;
  const filters = patch.filters ?? m.filters ?? [];
  const label = patch.label ?? m.label ?? "";
  const before = metricKey(m);
  const hidden = m.hidden ? { hidden: true } : {};
  if (!filters.length) {
    const plain = { ...d, metrics: d.metrics.map((x, j) => (j === i ? { kind: "metric" as const, ref: m.ref, ...hidden } : x)) };
    return renameColumn(plain, before, m.ref);
  }
  const id = label.trim() ? columnId(d, label, "metric", m.id) : m.id ?? columnId(d, m.ref.split(".")[1]!, "metric");
  const next = { ...d, metrics: d.metrics.map((x, j) => (j === i ? { kind: "metric" as const, ref: m.ref, id, label, filters, ...hidden } : x)) };
  return renameColumn(next, before, `filtered.${id}`);
}

/** Point formulas and the sort that use column key `from` (or input ref `input.<id>`) at `to`. */
function renameColumn(d: Draft, from: string, to: string): Draft {
  if (from === to) return d;
  const escaped = from.replace(/[.]/g, "\\.");
  const old = new RegExp(`(?<![a-z0-9_.])${escaped}(?![a-z0-9_])`, "g");
  return {
    ...d,
    // Only a filtered column's key or an input can appear in a formula under another name.
    metrics: isWidgetRef(from)
      ? d.metrics.map((m) => (m.kind === "formula" ? { ...m, expr: m.expr.replace(old, to) } : m))
      : d.metrics,
    sort: d.sort.map((s) => (s.key === from ? { ...s, key: to } : s)),
    rowFilters: d.rowFilters.map((f) => (f.key === from ? { ...f, key: to } : f)),
  };
}

/** A row filter still being set up: no column chosen yet, or not the numbers its condition needs. */
export const rowFilterIncomplete = (f: RowFilter) =>
  !f.key || f.values.length !== (f.op === "between" ? 2 : f.op === "not_null" || f.op === "is_null" ? 0 : 1);

/**
 * Add "<column> as a % of total" right after metric `i`: its value over its total across all rows,
 * shown as a percent.
 */
export function addPercentOfTotal(d: Draft, i: number, label: string): Draft {
  const m = d.metrics[i];
  if (m?.kind !== "metric") return d;
  const key = metricKey(m);
  const name = `${label} (% of total)`;
  const pct: MetricSelection = { kind: "formula", id: columnId(d, name, "formula"), label: name, expr: `${key} / total(${key})`, format: "ratio" };
  return { ...d, metrics: [...d.metrics.slice(0, i + 1), pct, ...d.metrics.slice(i + 1)] };
}

/** Hide or show column `i`: a hidden one still works for formulas, the sort and row filters. */
export function toggleHidden(d: Draft, i: number): Draft {
  return {
    ...d,
    metrics: d.metrics.map((m, j) => {
      if (j !== i) return m;
      const { hidden: _, ...rest } = m;
      return m.hidden ? (rest as MetricSelection) : { ...m, hidden: true };
    }),
  };
}

/** A new input for formulas to use, with an id from its name. */
export function addInput(d: Draft, label = "Rate", format: InputDef["format"] = "percent"): Draft {
  return { ...d, inputs: [...d.inputs, { id: inputId(d, label), label, format, default: 0 }] };
}

/**
 * Change input `i`. Renaming it moves its id to follow the new name, and formulas that use
 * `input.<old id>` follow along. Other edits keep the id, which saved values and other widgets
 * sharing the input are keyed by (an MCP-made input's id needn't match its label's slug).
 */
export function setInput(d: Draft, i: number, patch: Partial<Omit<InputDef, "id">>): Draft {
  const before = d.inputs[i];
  if (!before) return d;
  const label = patch.label ?? before.label;
  const renamed = patch.label !== undefined && patch.label !== before.label;
  const id = renamed && label.trim() ? inputId(d, label, before.id) : before.id;
  const next = { ...d, inputs: d.inputs.map((x, j) => (j === i ? { ...x, ...patch, label, id } : x)) };
  return renameColumn(next, `input.${before.id}`, `input.${id}`);
}

function inputId(d: Draft, label: string, except?: string): string {
  const taken = new Set(d.inputs.map((x) => x.id).filter((x) => x !== except));
  const base = slug(label);
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}_${n}`;
  return id;
}

/** The draft's inputs that some formula uses: the ones saved with the widget. */
export function usedInputs(d: Pick<Draft, "metrics" | "inputs">): InputDef[] {
  const used = new Set(d.metrics.flatMap((m) => (m.kind === "formula" ? inputRefsIn(m.expr) : [])));
  return d.inputs.filter((i) => used.has(i.id) && i.label.trim()).map((i) => ({ ...i, label: i.label.trim() }));
}

/**
 * Widgets are saved WITHOUT a date range or scope: the dashboard (and viewer) supply those.
 * Filters still being set up are left out, so they don't empty or break the preview.
 */
export function draftToQuery(draft: Draft): SemanticQueryInput {
  // A filtered column still being set up runs as its plain metric, so the sort and formulas that
  // use it as `filtered.<id>` follow it to the plain metric's key (else the sort is invalid).
  let d: Draft = { ...draft, metrics: draft.metrics.map(metricToQuery) };
  draft.metrics.forEach((m, i) => {
    const runs = d.metrics[i]!;
    if (m.kind === "metric" && metricKey(m) !== metricKey(runs)) d = renameColumn(d, metricKey(m), metricKey(runs));
  });
  return {
    pack: d.pack,
    metrics: d.metrics,
    dimensions: d.dimensions,
    filters: d.filters.filter((f) => !filterIncomplete(f)),
    sort: d.sort,
    limit: d.limit,
    limitBy: d.limit ? pivotLimitBy(d) : undefined,
    compareTo: d.compareTo,
    comparePeriods: d.compareTo && d.comparePeriods && d.comparePeriods > 1 ? d.comparePeriods : undefined,
    averagePer: d.averagePer,
    eachDay: d.eachDay || undefined,
    rowFilters: rowFiltersToQuery(d),
    inputs: usedInputs(d).length ? usedInputs(d) : undefined,
  };
}

/** The complete row filters on columns the widget still has: one being set up doesn't run yet. */
function rowFiltersToQuery(d: Draft): SemanticQueryInput["rowFilters"] {
  const keys = new Set(d.metrics.map(metricKey));
  const ready = d.rowFilters.filter((f) => !rowFilterIncomplete(f) && keys.has(f.key));
  return ready.length ? ready : undefined;
}

/**
 * In a pivot, "top N" means the N rows with the biggest Total column (every column of each),
 * not the N biggest cells. Rows are every grouping but the last (see Pivot). Ranked by the
 * sorted metric, else the first; formulas have no Total column, so they keep a plain row limit.
 */
export function pivotLimitBy(d: Draft): SemanticQueryInput["limitBy"] {
  if (d.viz.type !== "pivot" || d.dimensions.length < 2) return undefined;
  const sorted = d.sort[0] && d.metrics.find((m) => m.kind === "metric" && metricKey(m) === d.sort[0]!.key);
  const by = sorted ?? d.metrics[0];
  if (by?.kind !== "metric") return undefined;
  return {
    dimensions: d.dimensions.slice(0, -1).map((x) => (x.grain ? `${x.ref}:${x.grain}` : x.ref)),
    by: metricKey(by),
    dir: sorted ? d.sort[0]!.dir : "desc",
  };
}

export function draftToDefinition(d: Draft): WidgetDefinitionInput {
  return { title: d.title.trim() || "Untitled widget", query: draftToQuery(d), visualization: d.viz };
}

interface BuilderState {
  open: boolean;
  draft: Draft;
  widgetId?: string;
  /** Goes up each time the builder opens, so a late Ask answer can tell it's for an earlier opening. */
  session: number;
  /** The last Ask edit: what it changed, and the draft before it, for Undo. */
  askEdit?: { chips: string[]; before: Draft };
  openBuilder: (draft?: Draft, widgetId?: string) => void;
  close: () => void;
  update: (fn: (d: Draft) => Draft) => void;
  /** Replace the draft with an Ask edit of it. */
  applyAsk: (after: Draft, chips: string[]) => void;
  /** Back to the draft before the Ask edit, dropping the whole edit. */
  undoAsk: () => void;
}

export const useBuilder = create<BuilderState>((set) => ({
  open: false,
  draft: emptyDraft(),
  session: 0,
  openBuilder: (draft, widgetId) => set((s) => ({ open: true, draft: draft ?? emptyDraft(), widgetId, askEdit: undefined, session: s.session + 1 })),
  close: () => set({ open: false }),
  update: (fn) => set((s) => ({ draft: fn(s.draft) })),
  applyAsk: (after, chips) => set((s) => ({ draft: after, askEdit: { chips, before: s.draft } })),
  undoAsk: () => set((s) => (s.askEdit ? { draft: s.askEdit.before, askEdit: undefined } : {})),
}));

const REF = /[a-z_][a-z0-9_]*\.[a-z_][a-z0-9_]*/g;
export function metricRefsOf(metrics: MetricSelection[]): string[] {
  const out = new Set<string>();
  for (const m of metrics) {
    if (m.kind === "metric") out.add(m.ref);
    // A formula's `filtered.<id>` and `input.<id>` name the widget's own column or input, not a catalog metric.
    else for (const r of m.expr.match(REF) ?? []) if (!isWidgetRef(r)) out.add(r);
  }
  return [...out];
}

/** Whether the draft uses any field yet: from then on it's tied to its pack. */
export const usesFields = (d: Draft) => d.metrics.length > 0 || d.dimensions.length > 0 || d.filters.some((f) => f.ref);

/** The pack the draft reads: the one it names, else the workspace's first. */
export const draftPack = <P extends { name: string }>(d: Draft, packs: P[]): P => packs.find((p) => p.name === d.pack) ?? packs[0]!;
